// 自助修改密碼表單 — 2026-07-20 從 ChangePasswordModal 抽出。
//
// 為什麼抽出來: 個人選單原本有五項 (我的個資 / 我的假勤 / 我的薪資單 / 修改密碼 /
// 登出), 而且行為不一致 —— 個資與密碼開浮層、假勤與薪資單換頁。 使用者問「這幾個
// 可以整合嗎?」→ 把兩個低頻的 (個資 / 密碼) 收進同一張「我的帳戶」抽屜當分頁,
// 假勤與薪資單維持獨立入口 (使用者說兩個都常用, 收進分頁會從 1 點變 2 點)。
//
// 安全行為必須原封保留: 改密碼成功後倒數 3 秒 → 清 session → 跳登入頁。
// 後端改密碼會 bump app_user.sessions_valid_after (V200), 手上的 token 立刻失效,
// 不強制重登的話使用者會遇到一連串莫名其妙的 401。 這也擋掉「人離開座位、
// 同事改密碼接管帳號」的本機攻擊。

import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useActor } from '../lib/actor-store.ts';
import { type ApiError, api } from '../lib/api.ts';
import { useAuth } from '../lib/auth-store.ts';
import { ErrorBlock } from './ErrorBlock.tsx';
import { inputCls } from './form/Field.tsx';

export function ChangePasswordForm({
  onCancel,
  className = 'space-y-3',
}: {
  /** 有傳才顯示「取消」(modal 用); 抽屜分頁裡沒有取消的語意 */
  onCancel?: () => void;
  className?: string;
}) {
  const auth = useAuth();
  const actor = useActor((s) => s.actor);
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showHints, setShowHints] = useState(false);
  const [successCountdown, setSuccessCountdown] = useState<number | null>(null);

  const m = useMutation<unknown, ApiError, void>({
    mutationFn: () =>
      api('/api/v1/auth/change-password', {
        actor,
        method: 'POST',
        json: { current_password: currentPwd, new_password: newPwd },
      }),
    onSuccess: () => {
      // 倒數 3 秒後砍 session 強迫重登。 給 user 看「成功」訊息再踢。
      let n = 3;
      setSuccessCountdown(n);
      const t = setInterval(() => {
        n -= 1;
        setSuccessCountdown(n);
        if (n <= 0) {
          clearInterval(t);
          auth.clearSession();
          window.location.href = '/login';
        }
      }, 1000);
    },
  });

  const newPwdValid = newPwd.length >= 8;
  const confirmMatches = newPwd === confirmPwd && newPwd.length > 0;
  const canSubmit =
    currentPwd.length > 0 && newPwdValid && confirmMatches && !m.isPending && !successCountdown;

  if (successCountdown !== null) {
    return (
      <div className="space-y-3 text-sm">
        <p className="text-emerald-700 font-medium">✓ 密碼已更新。</p>
        <p className="text-slate-600">{successCountdown} 秒後登出，請用新密碼重新登入。</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) m.mutate();
      }}
      className={className}
    >
      {m.error && <ErrorBlock error={m.error} />}

      <label className="block">
        <span className="block text-sm font-medium text-slate-700 mb-1">目前密碼</span>
        <input
          type="password"
          value={currentPwd}
          onChange={(e) => setCurrentPwd(e.target.value)}
          autoComplete="current-password"
          required
          className={inputCls(false)}
        />
      </label>

      <div>
        <label className="block">
          <span className="block text-sm font-medium text-slate-700 mb-1">新密碼</span>
          <input
            type="password"
            value={newPwd}
            onChange={(e) => setNewPwd(e.target.value)}
            onFocus={() => setShowHints(true)}
            autoComplete="new-password"
            required
            minLength={8}
            className={inputCls(false)}
          />
        </label>
        {(showHints || newPwd.length > 0) && (
          <p className={`text-xs mt-1 ${newPwdValid ? 'text-emerald-700' : 'text-slate-500'}`}>
            {newPwdValid ? '✓ 至少 8 碼' : '⚠ 至少 8 碼'}
          </p>
        )}
      </div>

      <div>
        <label className="block">
          <span className="block text-sm font-medium text-slate-700 mb-1">確認新密碼</span>
          <input
            type="password"
            value={confirmPwd}
            onChange={(e) => setConfirmPwd(e.target.value)}
            autoComplete="new-password"
            required
            className={inputCls(false)}
          />
        </label>
        {confirmPwd.length > 0 && (
          <p className={`text-xs mt-1 ${confirmMatches ? 'text-emerald-700' : 'text-red-600'}`}>
            {confirmMatches ? '✓ 一致' : '✗ 兩次輸入不一致'}
          </p>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={m.isPending}
            className="px-4 py-1.5 text-sm border border-slate-300 rounded hover:bg-slate-50"
          >
            取消
          </button>
        )}
        <button
          type="submit"
          disabled={!canSubmit}
          className="px-4 py-1.5 text-sm bg-slate-900 text-white rounded hover:bg-slate-800 disabled:opacity-50"
        >
          {m.isPending ? '更新中…' : '更新密碼'}
        </button>
      </div>
    </form>
  );
}
